import { useEffect, useState } from 'react';

/**
 * Where each docked card should stick, so a stack of them tiles down the top of the
 * column instead of covering each other.
 *
 * Measured, not derived: card heights differ wildly and change under us as tables page
 * and charts resize, so a ResizeObserver keeps the offsets honest.
 * @param {Set<string>} pinnedIds The section ids currently docked.
 * @param {string} [refreshKey] Anything else that changes which cards are on the page,
 *   such as the host's serialised layout-visibility record. A hidden pinned card drops
 *   its `data-pinned` without `pinnedIds` changing, which otherwise leaves a gap.
 * @return {Record<string, number>} Section id to its top offset in px.
 */
export function useDockOffsets(
  pinnedIds: Set<string>,
  refreshKey = ''
): Record<string, number> {
  const [offsets, setOffsets] = useState<Record<string, number>>({});
  // The set identity changes on every toggle; the contents are what matter here.
  const key = `${[...pinnedIds].sort().join('|')}#${refreshKey}`;

  useEffect(() => {
    if (pinnedIds.size === 0) {
      setOffsets((prev) => (Object.keys(prev).length === 0 ? prev : {}));

      return undefined;
    }

    // The card column, not the whole document: a drawer or rail carrying the same
    // attribute must not enter the measurement.
    const column = document.querySelector('main') ?? document;

    const measure = () => {
      // Document order, which is the order they dock in.
      const docked = [...column.querySelectorAll<HTMLElement>('[data-pinned]')];
      let running = 0;
      const next: Record<string, number> = {};
      docked.forEach((node) => {
        const id = node.getAttribute('data-section');
        if (!id) return;
        next[id] = running;
        running += node.offsetHeight;
      });

      setOffsets((prev) => {
        const same =
          Object.keys(next).length === Object.keys(prev).length &&
          Object.entries(next).every(([id, top]) => prev[id] === top);

        return same ? prev : next;
      });
    };

    measure();

    // Re-measure when a docked card changes height, so the ones below it follow.
    const observer = new ResizeObserver(measure);
    column
      .querySelectorAll<HTMLElement>('[data-pinned]')
      .forEach((node) => observer.observe(node));

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return offsets;
}
